import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { renderDuration } from "./durationFormatting.js";

interface TimeSavingsCardProps {
  readonly rank: number | undefined;
  readonly totalCriteria: number | undefined;
  readonly secondsSavedPerCall: number | undefined;
  readonly pctFaster: number | undefined;
  readonly hrsPerWeek: number | undefined;
  /**
   * Drives the card's narrative branch:
   * - "savings": team is meaningfully slower than the company top-10 (positive
   *   gap to close).
   * - "parity": team is at top-performer speed (within ±1s of the benchmark).
   * - "ahead": team is meaningfully faster than the company top-10 — a
   *   strength worth surfacing.
   * - "noBenchmark": no top-performer benchmark is available (e.g. playbook
   *   has no outcome configured).
   */
  readonly state: "savings" | "parity" | "ahead" | "noBenchmark" | undefined;
}

const getRankStyleParameters = (
  rank: number,
  totalCriteria: number,
): { color: PillColor; textColor: string } => {
  if (rank === 1) {
    return {
      color: Color.Green,
      textColor: "text-green-700",
    };
  }
  if (rank <= Math.ceil(totalCriteria * 0.2)) {
    return {
      color: Color.Green,
      textColor: "text-success",
    };
  }
  if (rank <= Math.ceil(totalCriteria * 0.4)) {
    return {
      color: Color.Lime,
      textColor: "text-lime-600",
    };
  }
  return {
    color: Color.Gray,
    textColor: "text-muted-foreground",
  };
};

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

const formatHrs = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export const TimeSavingsCard: FC<TimeSavingsCardProps> = ({
  rank,
  totalCriteria,
  secondsSavedPerCall,
  pctFaster,
  hrsPerWeek,
  state,
}) => {
  const rankStyle =
    state === "savings" && rank !== undefined && totalCriteria !== undefined
      ? getRankStyleParameters(rank, totalCriteria)
      : { color: Color.Gray, textColor: "text-muted-foreground" };

  return (
    <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-x-8">
        <Label size={Size.SMALL} className="mb-2">
          Time savings
        </Label>
        <div className="mb-2 flex items-center gap-2">
          <Pill
            color={rankStyle.color}
            size={Size.SMALL}
            label={rank !== undefined ? `#${rank}` : "—"}
          />
          {rank !== undefined ? (
            <span
              className={twMerge("text-xs font-medium", rankStyle.textColor)}
            >
              rank of {totalCriteria} criteria
            </span>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-gray-600">
        {state === "savings" && secondsSavedPerCall !== undefined ? (
          <>
            Top agents are{" "}
            <span className="font-semibold">
              {/* state === "savings" guarantees pctFaster is defined (parent only enters this state when criterionAvgSec > 0). */}
              {formatPercent(pctFaster!)} faster
            </span>{" "}
            on this criteria (
            <span className="font-semibold">
              {renderDuration(secondsSavedPerCall)} less
            </span>{" "}
            per call).
            {hrsPerWeek !== undefined && hrsPerWeek >= 0.1 ? (
              <>
                {" "}
                Closing that gap could{" "}
                <span className="font-semibold text-success">
                  free up roughly {formatHrs(hrsPerWeek)} hrs/week across the
                  team
                </span>
                .
              </>
            ) : (
              <> Team-wide impact is minimal.</>
            )}
          </>
        ) : state === "ahead" ? (
          <>
            Your team is faster than top performers on this criterion — no time
            savings to capture, this is a strength.
          </>
        ) : state === "parity" ? (
          <>
            Your team matches top performers on this criterion — there&apos;s no
            time gap to close.
          </>
        ) : state === "noBenchmark" ? (
          <>Top agents are not measurably faster on this criteria.</>
        ) : null}
      </p>
    </div>
  );
};
